import React, { useMemo, useState, useEffect } from "react";

const FILTER_OPTIONS = [
  { value: "all", label: "All devices" },
  { value: "fw_lt_3000", label: "Firmware < 3000" },
  { value: "fw_gt_3000", label: "Firmware > 3000" },
  { value: "cells_eq_6", label: "Taycan" },
  { value: "cells_7_14", label: "14S" },
  { value: "cells_eq_18", label: "18S" },
  { value: "cells_15_17", label: "MEB" },
  { value: "online", label: "Online" },
  { value: "protect", label: "Protect" },
  { value: "alarm", label: "Alarm" },
  { value: "new_events", label: "Have new events" },

];

const COLUMN_STORAGE_KEY = "deviceList2Filtered:selectedColumns";

const DEFAULT_COLUMNS = ["SOC", "Voltage", "Power", "CellMaxVoltage", "CellMinVoltage", "dV"];

const COLUMN_DEFINITIONS = {
  SOC: { label: "SOC", keys: ["SOC"] },
  Voltage: { label: "Voltage", keys: ["Voltage"] },
  Amphours: { label: "Ah", keys: ["Amphours", "AmpHours"] },
  Current: { label: "Current", keys: ["Current", "CurrentAct"] },
  Power: { label: "Power", keys: ["Power", "PowerAct"] },
  CellMaxVoltage: { label: "Max Cell Voltage", keys: ["CellMaxVoltage", "VCellMax"] },
  CellMinVoltage: { label: "Min Cell Voltage", keys: ["CellMinVoltage", "VCellMin"] },
  dV: { label: "Cell Voltage Difference", keys: ["dV", "dv", "CellVoltageDifference"] },
  TempMax: { label: "Max Cell Temperature", keys: ["TempMax", "CellMaxTemp"] },
  TempMin: { label: "Min Cell Temperature", keys: ["TempMin", "CellMinTemp"] },
  ChargeCurrent: { label: "Charge Current", keys: ["ChargeCurrent"] },
  DischargeCurrent: { label: "Discharge Current", keys: ["DischargeCurrent"] },
  Status: { label: "Status", keys: ["Status"] },
  fw: { label: "FW", keys: ["fw"] },
  cellsUsed: { label: "CellsUsed", keys: ["cellsUsed"] },
  updated: { label: "Last update", keys: ["updated"] },
  online: { label: "Online", keys: ["online"] },
  new_events: { label: "New events", keys: ["new_events", "newEvents", "events", "event_count"] },
};

const PREDEFINED_COLUMN_ORDER = [
  "SOC",
  "Voltage",
  "Amphours",
  "Current",
  "Power",
  "CellMaxVoltage",
  "CellMinVoltage",
  "dV",
  "TempMax",
  "TempMin",
  "ChargeCurrent",
  "DischargeCurrent",
  "Status",
  "new_events",
  "online",
  "fw",
  "cellsUsed",
  "updated",
];

function getStoredColumns() {
  try {
    const raw = localStorage.getItem(COLUMN_STORAGE_KEY);
    if (!raw) {
      return DEFAULT_COLUMNS;
    }
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : DEFAULT_COLUMNS;
  } catch {
    return DEFAULT_COLUMNS;
  }
}

function toNumber(value) {
  const num = Number(value);
  return Number.isFinite(num) ? num : null;
}

function getFirstAvailableValue(device, keys) {
  for (const key of keys) {
    const value = device[key];
    if (value !== undefined && value !== null && value !== "") {
      return value;
    }
  }
  return null;
}

function getColumnRawValue(device, key) {
  if (key === "dV") {
    const direct = getFirstAvailableValue(device, COLUMN_DEFINITIONS.dV.keys);
    if (direct !== null) {
      return direct;
    }

    const maxCell = toNumber(getFirstAvailableValue(device, COLUMN_DEFINITIONS.CellMaxVoltage.keys));
    const minCell = toNumber(getFirstAvailableValue(device, COLUMN_DEFINITIONS.CellMinVoltage.keys));
    if (maxCell !== null && minCell !== null) {
      return Number((maxCell - minCell).toFixed(3));
    }
    return null;
  }

  const definition = COLUMN_DEFINITIONS[key];
  if (definition && Array.isArray(definition.keys)) {
    return getFirstAvailableValue(device, definition.keys);
  }

  return device[key] ?? null;
}

function getReadableStatus(device) {
  const status = device.Status;
  if (!device.online || status === "") {
    return "Disconnected";
  }
  return status || "N/A";
}

function hasNewEvents(device) {
  const directValues = [device.new_events, device.newEvents, device.events, device.event_count];

  for (const value of directValues) {
    const numeric = toNumber(value);
    if (numeric !== null && numeric > 0) {
      return true;
    }
  }

  if (typeof device.display_description === "string") {
    return /\(\s*\d+\s*\)\s*$/.test(device.display_description);
  }

  return false;
}

function getAvailableColumns(devices) {
  const excluded = new Set([
    "id",
    "description",
    "display_description",
    "device_id",
    "last_data_received_at",
  ]);

  const keySet = new Set(PREDEFINED_COLUMN_ORDER);
  devices.forEach((device) => {
    Object.keys(device).forEach((key) => {
      if (!excluded.has(key)) {
        keySet.add(key);
      }
    });
  });

  const keys = Array.from(keySet);
  keys.sort((a, b) => {
    const indexA = PREDEFINED_COLUMN_ORDER.indexOf(a);
    const indexB = PREDEFINED_COLUMN_ORDER.indexOf(b);
    const inPredefinedA = indexA !== -1;
    const inPredefinedB = indexB !== -1;

    if (inPredefinedA && inPredefinedB) {
      return indexA - indexB;
    }
    if (inPredefinedA) {
      return -1;
    }
    if (inPredefinedB) {
      return 1;
    }
    return a.localeCompare(b);
  });

  return keys;
}

function getColumnLabel(columnKey) {
  return COLUMN_DEFINITIONS[columnKey]?.label || columnKey;
}

function getSortValue(device, key) {
  if (key === "display_description") {
    return (device.display_description || "").toString().toLowerCase();
  }

  if (key === "Status") {
    return getReadableStatus(device).toLowerCase();
  }

  if (key === "updated") {
    const ts = Date.parse(device.last_data_received_at);
    if (Number.isFinite(ts)) {
      return ts;
    }
    return (device.updated || "").toString().toLowerCase();
  }

  if (key === "new_events") {
    const directValues = [device.new_events, device.newEvents, device.events, device.event_count];
    for (const value of directValues) {
      const numeric = toNumber(value);
      if (numeric !== null) {
        return numeric;
      }
    }
    return hasNewEvents(device) ? 1 : 0;
  }

  if (key === "online") {
    return device.online ? 1 : 0;
  }

  if (key === "Power") {
    const numeric = toNumber(getColumnRawValue(device, key));
    return numeric !== null ? numeric : Number.NEGATIVE_INFINITY;
  }

  const value = getColumnRawValue(device, key);

  if (value === null || value === undefined) {
    return Number.NEGATIVE_INFINITY;
  }

  if (typeof value === "boolean") {
    return value ? 1 : 0;
  }

  const numeric = toNumber(value);
  if (numeric !== null) {
    return numeric;
  }

  return value.toString().toLowerCase();
}

function formatCellValue(device, key) {
  const value = getColumnRawValue(device, key);

  if (key === "Voltage") {
    const numeric = toNumber(value);
    return numeric !== null ? `${numeric} V` : "N/A";
  }

  if (key === "SOC") {
    const numeric = toNumber(value);
    return numeric !== null ? `${numeric} %` : "N/A";
  }

  if (key === "Amphours") {
    const numeric = toNumber(value);
    return numeric !== null ? `${numeric} Ah` : "N/A";
  }

  if (key === "Current") {
    const numeric = toNumber(value);
    return numeric !== null ? `${numeric} A` : "N/A";
  }

  if (key === "Power") {
    const numeric = toNumber(value);
    return numeric !== null ? `${numeric} W` : "N/A";
  }

  if (key === "CellMaxVoltage") {
    const numeric = toNumber(value);
    return numeric !== null ? `${numeric} V` : "N/A";
  }

  if (key === "CellMinVoltage") {
    const numeric = toNumber(value);
    return numeric !== null ? `${numeric} V` : "N/A";
  }

  if (key === "dV") {
    const numeric = toNumber(value);
    return numeric !== null ? `${numeric} V` : "N/A";
  }

  if (key === "TempMax") {
    const numeric = toNumber(value);
    return numeric !== null ? `${numeric} °C` : "N/A";
  }

  if (key === "TempMin") {
    const numeric = toNumber(value);
    return numeric !== null ? `${numeric} °C` : "N/A";
  }

  if (key === "ChargeCurrent") {
    const numeric = toNumber(value);
    return numeric !== null ? `${numeric} A` : "N/A";
  }

  if (key === "DischargeCurrent") {
    const numeric = toNumber(value);
    return numeric !== null ? `${numeric} A` : "N/A";
  }

  if (key === "Status") {
    return getReadableStatus(device);
  }

  if (key === "updated") {
    return device.updated || "N/A";
  }

  if (key === "online") {
    return device.online ? "Yes" : "No";
  }

  if (key === "new_events") {
    const numeric = toNumber(device.new_events ?? device.newEvents ?? device.events ?? device.event_count);
    if (numeric !== null) {
      return numeric;
    }
    return hasNewEvents(device) ? "Yes" : "No";
  }

  if (value === null || value === undefined || value === "") {
    return "N/A";
  }

  if (typeof value === "boolean") {
    return value ? "Yes" : "No";
  }

  return value;
}

function matchesFilter(device, filterValue) {
  const fw = toNumber(device.fw);
  const cellsUsed = toNumber(device.cellsUsed);
  const status = getReadableStatus(device).toLowerCase();

  if (filterValue === "fw_lt_3000") {
    return fw !== null && fw < 3000;
  }

  if (filterValue === "fw_gt_3000") {
    return fw !== null && fw > 3000;
  }

  if (filterValue === "cells_eq_6") {
    return cellsUsed === 6;
  }

  if (filterValue === "cells_7_14") {
    return cellsUsed !== null && cellsUsed > 6 && cellsUsed <= 14;
  }

  if (filterValue === "cells_15_17") {
    return cellsUsed !== null && cellsUsed > 14 && cellsUsed < 18;
  }

  if (filterValue === "cells_eq_18") {
    return cellsUsed === 18;
  }

  if (filterValue === "online") {
    return Boolean(device.online);
  }

  if (filterValue === "protect") {
    return status === "protect";
  }

  if (filterValue === "alarm") {
    return status === "warning" || status === "alarm";
  }

  if (filterValue === "new_events") {
    return hasNewEvents(device);
  }

  return true;
}

export default function DeviceList2Filtered() {
  const [devices, setDevices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedColumns, setSelectedColumns] = useState(() => getStoredColumns());
  const [sortConfig, setSortConfig] = useState({ key: "display_description", direction: "asc" });

  const fetchData = async () => {
    try {
      const response = await fetch("/api/devicelist2", {
        credentials: "include",
      });
      const data = await response.json();
      setDevices(data);
      setLoading(false);
    } catch (error) {
      console.error("Error fetching data:", error);
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    localStorage.setItem(COLUMN_STORAGE_KEY, JSON.stringify(selectedColumns));
  }, [selectedColumns]);

  const availableColumns = useMemo(() => getAvailableColumns(devices), [devices]);

  useEffect(() => {
    setSelectedColumns((prev) => {
      const deduped = Array.from(new Set(prev));
      const valid = deduped.filter((key) => availableColumns.includes(key));

      if (valid.length > 0) {
        return valid;
      }

      const defaults = DEFAULT_COLUMNS.filter((key) => availableColumns.includes(key));
      if (defaults.length > 0) {
        return defaults;
      }

      return availableColumns.slice(0, 6);
    });
  }, [availableColumns]);

  const hasSearchQuery = searchQuery.trim().length > 0;

  const searchedDevices = useMemo(() => {
    if (!hasSearchQuery) {
      return devices;
    }

    const query = searchQuery.trim().toLowerCase();
    return devices.filter((device) => {
      const name = (device.display_description || device.description || "").toString().toLowerCase();
      const id = (device.device_id || "").toString().toLowerCase();
      return name.includes(query) || id.includes(query);
    });
  }, [devices, hasSearchQuery, searchQuery]);

  const filteredDevices = useMemo(() => {
    if (hasSearchQuery) {
      return searchedDevices;
    }

    return searchedDevices.filter((device) => matchesFilter(device, filter));
  }, [searchedDevices, hasSearchQuery, filter]);

  const sortedDevices = useMemo(() => {
    const sorted = [...filteredDevices];
    const { key, direction } = sortConfig;
    const directionFactor = direction === "asc" ? 1 : -1;

    sorted.sort((a, b) => {
      const left = getSortValue(a, key);
      const right = getSortValue(b, key);

      if (left < right) {
        return -1 * directionFactor;
      }
      if (left > right) {
        return 1 * directionFactor;
      }
      return 0;
    });

    return sorted;
  }, [filteredDevices, sortConfig]);

  const handleToggleColumn = (columnKey) => {
    setSelectedColumns((prev) => {
      if (prev.includes(columnKey)) {
        return prev.filter((key) => key !== columnKey);
      }
      return [...prev, columnKey];
    });
  };

  const handleSort = (key) => {
    setSortConfig((prev) => {
      if (prev.key === key) {
        return { key, direction: prev.direction === "asc" ? "desc" : "asc" };
      }
      return { key, direction: "asc" };
    });
  };

  const renderSortIndicator = (key) => {
    if (sortConfig.key !== key) {
      return "";
    }
    return sortConfig.direction === "asc" ? " ↑" : " ↓";
  };

  return (
    <div>
      <div style={{ marginBottom: "12px", display: "flex", gap: "12px", alignItems: "center", flexWrap: "wrap" }}>
        <label htmlFor="device-filter">Filter:</label>
        <select
          id="device-filter"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          disabled={hasSearchQuery}
          style={{ padding: "6px 10px" }}
        >
          {FILTER_OPTIONS.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>

        <label htmlFor="device-search">Search:</label>
        <input
          id="device-search"
          type="text"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder="Name or ID"
          style={{ padding: "6px 10px", minWidth: "220px" }}
        />

        <span>Showing {sortedDevices.length} / {devices.length}</span>
        {hasSearchQuery && <span>Search active: filters ignored</span>}


        <details style={{ marginLeft: "auto", position: "relative" }}>
          <summary
            style={{
              cursor: "pointer",
              border: "1px solid #bbb",
              borderRadius: "6px",
              padding: "6px 10px",
              background: "#fff",
              userSelect: "none",
            }}
          >
            Show parameters
          </summary>
          <div
            style={{
              position: "absolute",
              right: 0,
              top: "calc(100% + 6px)",
              background: "#fff",
              border: "1px solid #ddd",
              borderRadius: "8px",
              padding: "10px",
              boxShadow: "0 8px 20px rgba(0,0,0,0.12)",
              zIndex: 20,
              minWidth: "220px",
              maxHeight: "320px",
              overflowY: "auto",
            }}
          >
            {availableColumns.map((columnKey) => (
              <label
                key={columnKey}
                style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "8px" }}
              >
                <input
                  type="checkbox"
                  checked={selectedColumns.includes(columnKey)}
                  onChange={() => handleToggleColumn(columnKey)}
                />
                {getColumnLabel(columnKey)}
              </label>
            ))}
          </div>
        </details>
      </div>

      {loading ? (
        <div className="spinner-container"><div className="spinner"></div></div>
      ) : (
        <table>
          <thead>
            <tr>
              <th className="dot-container"></th>
              <th style={{ cursor: "pointer" }} onClick={() => handleSort("display_description")}>
                Device{renderSortIndicator("display_description")}
              </th>
              {selectedColumns.map((columnKey) => (
                <th
                  key={columnKey}
                  style={{ cursor: "pointer" }}
                  onClick={() => handleSort(columnKey)}
                >
                  {getColumnLabel(columnKey)}
                  {renderSortIndicator(columnKey)}
                </th>
              ))}
              <th style={{ cursor: "pointer" }} onClick={() => handleSort("device_id")}>
                ID{renderSortIndicator("device_id")}
              </th>
            </tr>
          </thead>
          <tbody>
            {sortedDevices.map((device) => (
              <DeviceRow key={device.device_id} device={device} selectedColumns={selectedColumns} />
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

function DeviceRow({ device, selectedColumns }) {
  const {
    display_description,
    Status,
    device_id,
    online,
  } = device;

  let backgroundColor = "#fff";
  if (Status === "Warning") backgroundColor = "#ff9";
  if (Status === "Protect") backgroundColor = "#f99";
  if (Status === "Running") backgroundColor = "#9f9";
  if (!online || Status === "") {
    backgroundColor = "#aaa";
  }

  const encodedId = encodeURIComponent(device_id);
  const link = `/device/data?id=${encodedId}`;
  const updateLink = `/device/update?id=${encodedId}`;

  const parseFirmwareVersion = (value) => {
    const parsed = parseInt(value, 10);
    return Number.isNaN(parsed) ? null : parsed;
  };

  const handleDeviceSelect = (event) => {
    const firmwareVersion = parseFirmwareVersion(device.fw);
    const shouldShowUpdatePrompt = false; // Disabled update prompt

    if (!shouldShowUpdatePrompt) {
      return;
    }

    const accepted = window.confirm(
      `A new update is available for this device (firmware ${firmwareVersion}).\n\nPress OK to open the update page.`
    );

    if (accepted) {
      event.currentTarget.href = updateLink;
    }
  };

  return (
    <tr style={{ backgroundColor }}>
      <td>
        <div className="dot" style={{ backgroundColor }}></div>
      </td>
      <td>
        <a
          href={link}
          onClick={handleDeviceSelect}
          target="_blank"
          rel="noopener noreferrer"
          style={{ color: "inherit", textDecoration: "none" }}
        >
          {display_description || "N/A"}
        </a>
      </td>
      {selectedColumns.map((columnKey) => (
        <td key={columnKey}>{formatCellValue(device, columnKey)}</td>
      ))}
      <td>{device_id}</td>
    </tr>
  );
}
